<template>
  <article
    class="chain-card"
    :class="[chainLevel.status, platform]"
    data-t="chain-card"
  >
    <template v-if="!isDefault">
      <StIcon
        v-if="chainLevel.status === 'inProgress'"
        name="live"
        :size="16"
        class="live-indicator status-icon"
        data-t="live-indicator"
      />
      <div v-else class="icon-wrapper status-icon">
        <StIcon
          :name="chainLevel.status === 'new' ? 'lock-solid' : 'check'"
          :size="isMobile ? 10 : 12"
          data-t="lock-icon"
        />
      </div>
    </template>
    <div v-if="isMobile" class="level">
      {{ chainLevel.level }}
    </div>

    <div class="content">
      <h4 class="title">{{ chainLevel.title }}</h4>
      <DepositChainAwards
        :awards="chainLevel.bonusesOutput"
        :is-step-bonus="chainLevel.isStepBonus"
      />
    </div>
    <img
      v-if="isMobile"
      class="image"
      :src="chestImage"
      alt=""
      width="52"
      height="52"
    />
  </article>
</template>

<script setup lang="ts">
import DepositChainAwards from './DepositChainAwards.vue'
import type { ChainLevel } from '../../../types'
import depositImage from '../../../assets/deposit.png'
import depositClaimImage from '../../../assets/deposit-claim.png'
import depositLockImage from '../../../assets/deposit-lock.png'

interface Props {
  chainLevel: ChainLevel
  isDefault?: boolean
}
const { chainLevel, isDefault = false } = defineProps<Props>()
const { isMobile, platform } = usePlatform()

const chestImage = computed(
  () =>
    ({
      inProgress: depositImage,
      new: depositLockImage,
      processed: depositClaimImage,
    })[chainLevel.status] ?? depositImage,
)
</script>

<style scoped>
.title {
  margin: 0;
  font: var(--desktop-text-md-medium);
  color: var(--text-tertiary);
  text-align: center;
}

.content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-100);
  align-items: center;

  width: 100%;
}

.live-indicator {
  color: var(--icon-brand);
}

.icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 20px;
  height: 20px;

  color: var(--icon-secondary);

  background: var(--background-base);
  border-radius: var(--border-radius-full);
}

.status-icon {
  position: absolute;
  top: 10px;
  right: 9px;
}

.chain-card {
  position: relative;

  min-width: 0;
  padding: var(--spacing-150) var(--spacing-250) var(--spacing-200)
    var(--spacing-250);

  background: var(--background-secondary);
  border-radius: var(--border-radius-150);

  &.processed,
  &.new {
    .content,
    .image,
    .level {
      opacity: 0.6;
    }
  }

  &.processed {
    .icon-wrapper {
      color: var(--icon-primary);
      background-color: var(--background-success);
    }
  }
  /* stylelint-disable-next-line */
  &.inProgress {
    background: linear-gradient(95deg, #721528 0%, #3d0b15 100%);
    box-shadow:
      0 24px 64px -4px rgb(0 0 0 / 40%),
      0 8px 16px -4px rgb(0 0 0 / 20%);
  }

  &.mobile {
    display: flex;
    gap: var(--spacing-150);
    align-items: center;

    padding: var(--spacing-025) var(--spacing-150);
    padding-left: var(--spacing-600);

    .title {
      font: var(--mobile-text-regular);
    }

    .content {
      display: flex;
      gap: var(--spacing-025);
      align-items: flex-start;

      max-width: calc(100% - 64px);
      padding: 0;
    }

    .level {
      position: absolute;
      top: 16px;
      left: 12px;

      display: flex;
      align-items: center;
      justify-content: center;

      width: 24px;
      height: 24px;
      padding: var(--spacing-050) var(--spacing-025);

      font: var(--mobile-text-semibold);

      background: var(--background-ghost-tertiary);
      backdrop-filter: blur(10px);
      border-radius: var(--border-radius-full);
    }

    .status-icon {
      position: absolute;
      z-index: 1;
      top: 30px;
      right: unset;
      left: 26px;
    }

    .live-indicator {
      left: 24px;
      color: var(--icon-primary);
    }

    .icon-wrapper {
      width: 14px;
      height: 14px;
    }
    /* stylelint-disable-next-line */
    &.inProgress {
      .level {
        background: var(--icon-brand);
      }
    }
  }
}
</style>
